import Phaser from 'phaser';

class MainScene extends Phaser.Scene {
    constructor() {
        super({ key: 'MainScene' });
        this.initializeGameState();
    }

    initializeGameState() {
        this.balls = [];
        this.queens = [];
        this.isDragging = false;
        this.ballsInHole = 0;
        this.queensHit = 0;
        this.totalQueens = 5; // Will be calculated based on screen size
        // Set larger margins for mobile devices
        this.marginMultiplier = (game.device.os.android || game.device.os.iOS) ? 0.075 : 0.05;
        this.flingCount = 0;
        this.activeBall = null;
        this.currentBallIndex = 0;
        this.maxDragDistance = 150;
        this.highScores = JSON.parse(localStorage.getItem('colorFlingerScores')) || [];
        this.gameEnded = false;
        this.scoreTexts = [];
        this.queensHitThisFling = 0;
        this.gameWidth = window.innerWidth;
        this.gameHeight = window.innerHeight;
        this.ballColors = [0xff2d55, 0xff8c00, 0xffd60a, 0x2ecc71, 0x007aff];  // Rainbow order
        this.waitingForBallsToSettle = false;
        this.ballSettleTimeout = null;
        this.lastStateCheck = 0;
        this.soundEnabled = true;
        this.currentOscillator = null;
        this.currentGain = null;
        
        // Initialize audio context
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.masterGain = this.audioContext.createGain();
            this.masterGain.connect(this.audioContext.destination);
            this.masterGain.gain.value = 0.3; // Default volume
        } catch (error) {
            console.warn('Web Audio API not supported');
            this.soundEnabled = false;
        }
    }

    preload() {
        // Create loading text
        const loadingText = this.add.text(
            this.game.config.width / 2,
            this.game.config.height / 2,
            'Loading...',
            {
                fontSize: '32px',
                fill: '#fff',
                fontFamily: 'Arial'
            }
        ).setOrigin(0.5);

        // Create particle texture
        const particles = this.add.graphics({ x: 0, y: 0, add: false });
        particles.fillStyle(0xffffff, 1);
        particles.fillCircle(4, 4, 4);
        particles.generateTexture('particle', 8, 8);

        // Remove loading text when all assets are loaded
        this.load.once('complete', () => {
            loadingText.destroy();
        });
    }

    create() {
        // Clean up any existing input handlers
        this.input.removeAllListeners();
        
        // Reset game state
        this.initializeGameState();

        // Calculate play area and number of queens
        this.calculatePlayAreaAndQueens();

        // Add debug key
        this.input.keyboard.on('keydown-D', () => {
            this.debugBallStates();
        });

        // Add reset key
        this.input.keyboard.on('keydown-R', () => {
            console.log('Manual recovery triggered');
            this.validateAndFixGameState();
        });

        // Handle resize events
        this.scale.on('resize', (gameSize) => {
            // Only resize if not in the middle of a fling
            if (!this.isDragging && !this.waitingForBallsToSettle) {
                this.resize(gameSize);
            }
        });
        
        // Initial setup
        this.resize(this.scale.gameSize);

        // Modern dark background that covers entire game area
        const bg = this.add.rectangle(
            this.gameWidth/2, 
            this.gameHeight/2, 
            this.gameWidth, 
            this.gameHeight, 
            0x1a1a2e
        );
        bg.setDepth(-1);

        // Calculate margin once for use throughout
        this.margin = Math.min(this.gameWidth, this.gameHeight) * this.marginMultiplier;

        // Playfield with neon effect
        this.playfield = this.add.graphics();
        this.playfield.lineStyle(4, 0x2ecc71);
        this.playfield.strokeRect(
            this.margin, 
            this.margin, 
            this.gameWidth - this.margin * 2, 
            this.gameHeight - this.margin * 2
        );

        // Set physics bounds
        this.matter.world.setBounds(
            this.margin, 
            this.margin, 
            this.gameWidth - this.margin * 2, 
            this.gameHeight - this.margin * 2, 
            32,
            {
                restitution: 0.8,
                friction: 0.05
            }
        );
        this.matter.world.setGravity(0, 0);

        // Setup game objects
        this.setupGameObjects();

        try {
            // Add mute button
            const muteButton = this.add.text(this.gameWidth - 100, 16, '🔊', {
                fontSize: '32px',
                padding: { x: 10, y: 5 },
                backgroundColor: '#00000066'
            })
            .setInteractive()
            .setDepth(1000);

            muteButton.on('pointerdown', () => {
                this.soundEnabled = !this.soundEnabled;
                if (this.soundEnabled) {
                    muteButton.setText('🔊');
                    this.masterGain.gain.value = 0.3;
                    this.playSound('bgMusic');
                } else {
                    muteButton.setText('🔈');
                    this.masterGain.gain.value = 0;
                }
            });

            // Start background music
            if (this.soundEnabled) {
                this.playSound('bgMusic');
            }
        } catch (error) {
            console.warn('Audio setup failed:', error);
            // Game can continue without audio
        }

        // Setup input handlers
        this.setupInputHandlers();
    }

    setupGameObjects() {
        // Black hole with silver glow
        const holeY = this.margin + (this.gameHeight - this.margin * 2) * 0.2;
        this.hole = this.add.circle(this.gameWidth/2, holeY, 25, 0x000000);
        this.holeGlow = this.add.circle(this.gameWidth/2, holeY, 30, 0xC0C0C0, 0.4);
        this.holeGlow2 = this.add.circle(this.gameWidth/2, holeY, 35, 0xC0C0C0, 0.2);
        this.holeRim = this.add.circle(this.gameWidth/2, holeY, 25, 0xC0C0C0);
        this.holeRim.setStrokeStyle(2, 0xFFFFFF, 0.8);

        // Add physics to the hole with collision filtering
        this.holeBody = this.matter.add.circle(this.gameWidth/2, holeY, 25, {
            label: 'hole',
            restitution: 0.8,
            friction: 0.005,
            frictionAir: 0.001,
            density: 0.01,
            collisionFilter: {
                category: 0x0002,  // Hole category
                mask: 0x0001      // Only collide with walls
            }
        });

        // Set up collision categories for walls by recreating the bounds
        const margin = Math.min(this.gameWidth, this.gameHeight) * this.marginMultiplier;
        this.matter.world.remove(this.matter.world.walls);
        this.matter.world.setBounds(
            margin, 
            margin, 
            this.gameWidth - margin * 2, 
            this.gameHeight - margin * 2, 
            32,
            {
                label: 'wall',
                isStatic: true,
                collisionFilter: {
                    category: 0x0001,  // Wall category
                    mask: 0xFFFF      // Collide with everything
                }
            }
        );

        // Set up collision handling
        this.matter.world.on('collisionstart', (event) => {
            event.pairs.forEach((pair) => {
                const bodyA = pair.bodyA;
                const bodyB = pair.bodyB;

                // Ball-to-queen collision
                if ((bodyA.label === 'Circle Body' && bodyB.label === 'queen') ||
                    (bodyA.label === 'queen' && bodyB.label === 'Circle Body')) {
                    const ball = bodyA.label === 'Circle Body' ? bodyA : bodyB;
                    const queen = bodyA.label === 'queen' ? bodyA : bodyB;
                    
                    // Apply bounce effect
                    const velocity = ball.velocity;
                    const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);
                    
                    if (speed > 0.5) {
                        // Find the queen object
                        const queenObj = this.queens.find(q => q.body === queen);
                        if (queenObj && !queenObj.hit) {
                            queenObj.hit = true;
                            this.queensHit++;
                            this.queensHitThisFling++;
                            this.playSound('queenHit');
                            
                            // Animate the queen
                            this.tweens.add({
                                targets: [queenObj.graphics, queenObj.glow],
                                alpha: 0,
                                scale: 1.5,
                                duration: 500,
                                onComplete: () => {
                                    if (queenObj.body) {
                                        this.matter.world.remove(queenObj.body);
                                        queenObj.body = null;
                                    }
                                    queenObj.graphics.destroy();
                                    queenObj.glow.destroy();
                                }
                            });
                        }
                    }
                }

                // Ball-to-ball collision
                if (bodyA.label === 'Circle Body' && bodyB.label === 'Circle Body') {
                    const velocity = Math.sqrt(
                        Math.pow(bodyA.velocity.x - bodyB.velocity.x, 2) +
                        Math.pow(bodyA.velocity.y - bodyB.velocity.y, 2)
                    );
                    
                    const currentTime = Date.now();
                    if (velocity > 1 && currentTime - this.lastBallHitTime > 100) {
                        this.playSound('ballHit');
                        this.lastBallHitTime = currentTime;
                    }
                }
            });
        });

        // Create queens
        this.createQueens();

        // Create balls
        const colors = [0xff2d55, 0x2ecc71, 0x007aff, 0xaf52de, 0xffd60a];
        this.resetBalls(colors);

        // Create UI elements
        this.createUI();
    }

    createUI() {
        // Modern UI
        this.scoreText = this.add.text(16, 16, 'Balls: 0/5', {
            fontSize: '24px',
            fill: '#fff',
            fontFamily: 'Arial',
            padding: { x: 10, y: 5 },
            backgroundColor: '#00000066'
        });

        this.queensText = this.add.text(16, 56, `Queens hit: 0/${this.totalQueens}`, {
            fontSize: '24px',
            fill: '#fff',
            fontFamily: 'Arial',
            padding: { x: 10, y: 5 },
            backgroundColor: '#00000066'
        });

        this.flingText = this.add.text(16, 96, 'Flings: 0', {
            fontSize: '24px',
            fill: '#fff',
            fontFamily: 'Arial',
            padding: { x: 10, y: 5 },
            backgroundColor: '#00000066'
        });

        // High scores button with neon effect
        const scoresButton = this.add.text(650, 16, 'High Scores', {
            fontSize: '20px',
            fill: '#fff',
            backgroundColor: '#00000066',
            padding: { x: 10, y: 5 }
        }).setInteractive();

        // Add hover effect
        scoresButton.on('pointerover', () => {
            scoresButton.setScale(1.1);
        });
        
        scoresButton.on('pointerout', () => {
            scoresButton.setScale(1);
        });

        scoresButton.on('pointerdown', () => {
            this.showHighScores();
        });

        this.rubberBand = this.add.graphics();
    }

    calculatePlayAreaAndQueens() {
        const margin = Math.min(this.gameWidth, this.gameHeight) * 0.05;
        const playArea = (this.gameWidth - margin * 2) * (this.gameHeight - margin * 2);
        const baseArea = 800 * 600; // Original base area
        const baseQueens = 10; // Doubled from 5 to 10 minimum queens
        
        // Calculate queens based on play area ratio, with min 14 and max 134 (doubled from 7 and 67)
        this.totalQueens = Math.min(134, Math.max(14, 
            Math.round(2.66 * baseQueens * Math.sqrt(playArea / baseArea)) // Doubled multiplier from 1.33 to 2.66
        ));
        
        // Adjust maxDragDistance based on screen size
        this.maxDragDistance = Math.min(200, Math.max(150, 
            Math.sqrt(playArea) * 0.15
        ));
    }

    createQueens() {
        // Generate colors for all queens
        const queenColors = [];
        for (let i = 0; i < this.totalQueens; i++) {
            const hue = (i / this.totalQueens) * 360;
            const color = Phaser.Display.Color.HSLToColor(hue/360, 1, 0.7).color;
            queenColors.push(color);
        }
        
        const margin = Math.min(this.gameWidth, this.gameHeight) * (this.marginMultiplier * 2); // Double the base margin for queens
        const safeMargin = margin + 25; // Additional safety margin for queen size
        const minX = safeMargin;
        const maxX = this.gameWidth - safeMargin;
        const minY = safeMargin;
        const maxY = Math.min(this.gameHeight * 0.7, this.gameHeight - safeMargin * 2); // Keep queens in upper area with safe margin

        const positions = [];
        const minDistance = Math.min(100, Math.sqrt((maxX - minX) * (maxY - minY) / this.totalQueens) * 0.8);
        
        // Generate positions for all queens
        for (let i = 0; i < this.totalQueens; i++) {
            let newPos;
            let validPosition = false;
            let attempts = 0;
            
            while (!validPosition && attempts < 100) {
                attempts++;
                newPos = {
                    x: Phaser.Math.Between(minX, maxX),
                    y: Phaser.Math.Between(minY, maxY)
                };
                
                // Check distance from hole
                const holeDistance = Phaser.Math.Distance.Between(
                    newPos.x, newPos.y,
                    this.hole.x, this.hole.y
                );
                
                // Check distance from ball spawn area
                const ballSpawnDistance = Phaser.Math.Distance.Between(
                    newPos.x, newPos.y,
                    this.gameWidth/2, this.gameHeight * 0.8
                );
                
                // Check distance from other queens
                let tooClose = false;
                for (const pos of positions) {
                    const distance = Phaser.Math.Distance.Between(
                        newPos.x, newPos.y,
                        pos.x, pos.y
                    );
                    if (distance < minDistance) {
                        tooClose = true;
                        break;
                    }
                }
                
                if (holeDistance > minDistance * 1.5 && ballSpawnDistance > minDistance * 1.5 && !tooClose) {
                    validPosition = true;
                    positions.push(newPos);
                }
            }

            if (!validPosition) {
                // Fallback position using grid layout
                const cols = Math.ceil(Math.sqrt(this.totalQueens));
                const rows = Math.ceil(this.totalQueens / cols);
                const col = i % cols;
                const row = Math.floor(i / cols);
                positions.push({
                    x: minX + (col + 0.5) * (maxX - minX) / cols,
                    y: minY + (row + 0.5) * (maxY - minY) / rows
                });
            }
        }

        // Create queens at calculated positions
        positions.forEach((pos, i) => {
            // Create container for queen graphics
            const container = this.add.container(pos.x, pos.y);
            
            // Scale crown size based on screen size
            const crownSize = Math.min(20, Math.max(15, 
                Math.sqrt(this.gameWidth * this.gameHeight) * 0.02
            ));
            
            // Create queen crown graphics
            const queen = this.add.graphics();
            queen.lineStyle(3, queenColors[i]);
            queen.fillStyle(queenColors[i], 0.8);
            
            queen.beginPath();
            queen.moveTo(-crownSize, 0);
            queen.lineTo(-crownSize * 0.75, -crownSize * 0.75);
            queen.lineTo(-crownSize * 0.25, -crownSize * 0.25);
            queen.lineTo(crownSize * 0.25, -crownSize * 0.75);
            queen.lineTo(crownSize * 0.75, -crownSize * 0.25);
            queen.lineTo(crownSize, 0);
            queen.closePath();
            queen.strokePath();
            queen.fillPath();

            // Create glow effect
            const queenGlow = this.add.circle(0, 0, crownSize * 1.25, queenColors[i], 0.3);
            
            // Add graphics to container
            container.add([queenGlow, queen]);
            
            // Create physics body with collision filtering
            const physicsBody = this.matter.add.circle(pos.x, pos.y, crownSize, {
                isStatic: true,
                label: 'queen',
                restitution: 0.8,
                friction: 0.05,
                collisionFilter: {
                    category: 0x0004,  // Queen category
                    mask: 0x0004       // Only collide with balls
                }
            });

            this.queens.push({
                container,
                graphics: queen,
                glow: queenGlow,
                body: physicsBody,
                hit: false,
                color: queenColors[i],
                position: pos
            });
        });
    }

    resetBalls(colors) {
        this.balls.forEach(ball => {
            if (ball.graphics) ball.graphics.destroy();
            if (ball.glow) ball.glow.destroy();
            if (ball.highlight) ball.highlight.destroy();
            if (ball.body) this.matter.world.remove(ball.body);
        });
        
        this.balls = [];
        const margin = Math.min(this.gameWidth, this.gameHeight) * 0.05;
        const startX = this.gameWidth / 2;
        const startY = this.gameHeight - margin - 50; // 50px above bottom margin
        
        colors.forEach((color, i) => {
            // Ensure balls don't spawn beyond margins
            const ballX = Math.min(Math.max(startX + (i-2)*40, margin + 20), this.gameWidth - margin - 20);
            const ball = this.add.circle(ballX, startY, 15, color);
            const glow = this.add.circle(ballX, startY, 18, color, 0.3);
            const highlight = this.add.circle(ballX, startY, 20, color, 0);
            highlight.setStrokeStyle(3, color);
            highlight.setVisible(false);

            const physicsBody = this.matter.add.circle(startX + (i-2)*40, startY, 15, {
                label: 'Circle Body',
                restitution: 0.8,
                friction: 0.05,
                density: 0.005,
                frictionAir: 0.003,
                collisionFilter: {
                    category: 0x0004,  // Ball category
                    mask: 0x0005       // Collide with walls (0x0001) and other balls/queens (0x0004)
                }
            });
            
            this.balls.push({
                graphics: ball,
                glow: glow,
                highlight: highlight,
                body: physicsBody,
                color: color,
                moving: false,
                used: false
            });
        });

        // Set initial active ball
        this.setActiveBall(0);
    }

    setActiveBall(index) {
        // Hide highlight on all balls
        this.balls.forEach(ball => {
            if (ball.highlight) {
                ball.highlight.setVisible(false);
                this.tweens.killTweensOf(ball.highlight);
            }
        });

        // Find next valid ball starting from the given index
        let nextIndex = index;
        let loopCount = 0;
        let foundBall = null;
        
        while (loopCount < this.balls.length) {
            if (nextIndex >= this.balls.length) {
                nextIndex = 0;
            }
            
            const ball = this.balls[nextIndex];
            if (ball && !ball.used && !ball.inHole && ball.body && !ball.moving) {
                foundBall = ball;
                break;
            }
            
            nextIndex++;
            loopCount++;
        }

        // If we found a valid ball, make it active
        if (foundBall) {
            this.currentBallIndex = nextIndex;
            
            if (foundBall.highlight) {
                this.tweens.killTweensOf(foundBall.highlight);
                foundBall.highlight.setVisible(true);
                foundBall.highlight.setScale(1);
                foundBall.highlight.setAlpha(1);
                
                this.tweens.add({
                    targets: foundBall.highlight,
                    scaleX: 1.2,
                    scaleY: 1.2,
                    alpha: 0.8,
                    duration: 600,
                    yoyo: true,
                    repeat: -1,
                    ease: 'Sine.easeInOut'
                });
            }
        } else {
            this.currentBallIndex = -1;
        }
    }

    findClosestBall(pointer) {
        // Only allow interaction with the current active ball
        const ball = this.balls[this.currentBallIndex];
        if (ball && !ball.used && !ball.moving) {
            const distance = Phaser.Math.Distance.Between(
                ball.graphics.x, ball.graphics.y,
                pointer.x, pointer.y
            );
            if (distance < 30) {
                return ball;
            }
        }
        return null;
    }

    constrainDragPoint(pointer, ballPos) {
        const angle = Phaser.Math.Angle.Between(ballPos.x, ballPos.y, pointer.x, pointer.y);
        const distance = Phaser.Math.Distance.Between(ballPos.x, ballPos.y, pointer.x, pointer.y);
        const constrainedDistance = Math.min(distance, this.maxDragDistance);

        return {
            x: ballPos.x + Math.cos(angle) * constrainedDistance,
            y: ballPos.y + Math.sin(angle) * constrainedDistance
        };
    }

    setupInputHandlers() {
        // Remove any existing listeners first
        this.input.removeAllListeners('pointerdown');
        this.input.removeAllListeners('pointermove');
        this.input.removeAllListeners('pointerup');
        this.input.removeAllListeners('pointerout');

        // Handle both mouse and touch input
        const startDrag = (pointer) => {
            if (this.gameEnded || this.waitingForBallsToSettle) return;
            
            const closestBall = this.findClosestBall(pointer);
            if (closestBall) {
                this.isDragging = true;
                this.activeBall = closestBall;
                this.matter.body.setStatic(this.activeBall.body, true);
            }
        };

        const moveDrag = (pointer) => {
            if (!this.isDragging || !this.activeBall) return;

            const ballPos = {
                x: this.activeBall.graphics.x,
                y: this.activeBall.graphics.y
            };

            const constrainedPoint = this.constrainDragPoint(pointer, ballPos);

            this.rubberBand.clear();
            this.rubberBand.lineStyle(2, 0xffffff);
            this.rubberBand.beginPath();
            this.rubberBand.moveTo(ballPos.x, ballPos.y);
            this.rubberBand.lineTo(constrainedPoint.x, constrainedPoint.y);
            this.rubberBand.strokePath();
        };

        const endDrag = (pointer) => {
            if (!this.isDragging || !this.activeBall) return;

            const ballPos = {
                x: this.activeBall.graphics.x,
                y: this.activeBall.graphics.y
            };

            const constrainedPoint = this.constrainDragPoint(pointer, ballPos);
            const angle = Phaser.Math.Angle.Between(
                constrainedPoint.x, constrainedPoint.y,
                ballPos.x, ballPos.y
            );
            
            const power = Phaser.Math.Distance.Between(
                ballPos.x, ballPos.y,
                constrainedPoint.x, constrainedPoint.y
            ) / this.maxDragDistance;

            const force = {
                x: Math.cos(angle) * power * 25,
                y: Math.sin(angle) * power * 25
            };
            
            // Store the sling direction for hole movement
            this.lastSlingAngle = angle;
            this.lastSlingPower = power;
            
            this.matter.body.setStatic(this.activeBall.body, false);
            this.matter.body.setVelocity(this.activeBall.body, force);
            this.activeBall.moving = true;
            this.activeBall.used = true;
            
            // Stop the highlight animation and hide it
            if (this.activeBall.highlight) {
                this.tweens.killTweensOf(this.activeBall.highlight);
                this.activeBall.highlight.setVisible(false);
            }
            
            this.rubberBand.clear();
            this.isDragging = false;
            this.activeBall = null;

            // Reset queens hit counter for new fling
            this.queensHitThisFling = 0;

            // Increment fling counter
            this.flingCount++;
            this.flingText.setText(`Flings: ${this.flingCount}`);

            // Start waiting for balls to settle
            this.waitingForBallsToSettle = true;
        };

        // Add new input handlers
        this.input.on('pointerdown', startDrag);
        this.input.on('pointermove', moveDrag);
        this.input.on('pointerup', endDrag);
        this.input.on('pointerout', endDrag);
    }

    showHighScores() {
        // Clear any existing score texts
        this.scoreTexts.forEach(text => text.destroy());
        this.scoreTexts = [];

        // Create modern semi-transparent backdrop with gradient
        const bgGradient = this.add.graphics();
        bgGradient.fillGradientStyle(0x000000, 0x000000, 0x162447, 0x162447, 0.9);
        bgGradient.fillRect(200, 100, 400, 400);
        this.scoreTexts.push(bgGradient);

        // Add neon border
        const border = this.add.graphics();
        border.lineStyle(4, 0x2ecc71, 0.8);
        border.strokeRect(200, 100, 400, 400);
        this.scoreTexts.push(border);

        // Title with neon effect
        const titleGlow = this.add.text(400, 150, 'High Scores', {
            fontSize: '36px',
            fontFamily: 'Arial',
            fill: '#fff',
            stroke: '#2ecc71',
            strokeThickness: 8
        }).setOrigin(0.5).setAlpha(0.4);
        
        const title = this.add.text(400, 150, 'High Scores', {
            fontSize: '36px',
            fontFamily: 'Arial',
            fill: '#fff'
        }).setOrigin(0.5);

        this.scoreTexts.push(titleGlow, title);

        // Sort scores by flings (lower is better)
        const sortedScores = [...this.highScores].sort((a, b) => a.flings - b.flings);

        sortedScores.slice(0, 5).forEach((score, i) => {
            const y = 220 + i * 45;
            
            // Score background
            const scoreBg = this.add.rectangle(400, y, 300, 35, 0x2ecc71, 0.1);
            this.scoreTexts.push(scoreBg);
            
            // Score text with glow
            const scoreGlow = this.add.text(400, y, 
                `${i + 1}. ${score.name}: ${score.flings} flings`, {
                fontSize: '24px',
                fontFamily: 'Arial',
                fill: '#fff',
                stroke: '#2ecc71',
                strokeThickness: 4
            }).setOrigin(0.5).setAlpha(0.4);
            
            const scoreText = this.add.text(400, y, 
                `${i + 1}. ${score.name}: ${score.flings} flings`, {
                fontSize: '24px',
                fontFamily: 'Arial',
                fill: '#fff'
            }).setOrigin(0.5);

            this.scoreTexts.push(scoreGlow, scoreText);
        });

        // Modern close button with neon effect
        const closeBtn = this.add.container(400, 450);
        
        const closeBtnBg = this.add.rectangle(0, 0, 100, 40, 0x2ecc71, 0.2);
        const closeBtnGlow = this.add.text(0, 0, 'Close', {
            fontSize: '24px',
            fontFamily: 'Arial',
            fill: '#fff',
            stroke: '#2ecc71',
            strokeThickness: 4
        }).setOrigin(0.5).setAlpha(0.4);
        
        const closeBtnText = this.add.text(0, 0, 'Close', {
            fontSize: '24px',
            fontFamily: 'Arial',
            fill: '#fff'
        }).setOrigin(0.5);

        closeBtn.add([closeBtnBg, closeBtnGlow, closeBtnText]);
        closeBtn.setSize(100, 40);
        closeBtn.setInteractive();
        
        // Hover effects
        closeBtn.on('pointerover', () => {
            closeBtnBg.setFillStyle(0x2ecc71, 0.3);
            closeBtn.setScale(1.05);
        });
        
        closeBtn.on('pointerout', () => {
            closeBtnBg.setFillStyle(0x2ecc71, 0.2);
            closeBtn.setScale(1);
        });

        closeBtn.on('pointerdown', () => {
            this.scoreTexts.forEach(text => text.destroy());
            this.scoreTexts = [];
        });

        this.scoreTexts.push(closeBtn);
    }

    promptForName() {
        // Create input element
        const nameInput = document.createElement('input');
        nameInput.type = 'text';
        nameInput.placeholder = 'Enter your name';
        nameInput.style.position = 'absolute';
        nameInput.style.left = '50%';
        nameInput.style.top = '50%';
        nameInput.style.transform = 'translate(-50%, -50%)';
        nameInput.style.fontSize = '24px';
        nameInput.style.padding = '10px';
        nameInput.style.width = '200px';
        nameInput.style.textAlign = 'center';
        nameInput.maxLength = 15;

        document.body.appendChild(nameInput);
        nameInput.focus();

        nameInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                const name = nameInput.value.trim() || 'Anonymous';
                this.saveScore(name);
                nameInput.remove();
                this.showHighScores();
            }
        });
    }

    saveScore(name) {
        this.highScores.push({
            name: name,
            flings: this.flingCount,
            date: new Date().toISOString()
        });

        // Keep only top 100 scores
        this.highScores.sort((a, b) => a.flings - b.flings);
        if (this.highScores.length > 100) {
            this.highScores.pop();
        }

        localStorage.setItem('colorFlingerScores', JSON.stringify(this.highScores));
    }

    showSlayMessage(count) {
        let message = '';
        let color = '';
        let scale = 1;
        
        switch(count) {
            case 2:
                message = 'DOUBLE COLLECT! 💅';
                color = '#ff69b4';
                scale = 0.9;
                break;
            case 3:
                message = 'TRIPLE COLLECT! 👑';
                color = '#ff1493';
                scale = 1.0;
                break;
            case 4:
                message = 'QUADRA COLLECT! ✨';
                color = '#ff00ff';
                scale = 1.1;
                break;
            case 5:
                message = 'PENTA COLLECT!!! 🌟';
                color = '#ff00ff';
                scale = 1.2;
                break;
        }

        if (message) {
            this.playSound('multiSlay');

            // Create flash effect
            const flash = this.add.rectangle(0, 0, this.gameWidth, this.gameHeight, 0xffffff);
            flash.setAlpha(0.3);
            flash.setDepth(999);
            
            this.tweens.add({
                targets: flash,
                alpha: 0,
                duration: 200,
                ease: 'Power2',
                onComplete: () => flash.destroy()
            });

            // Calculate center position
            const centerX = this.gameWidth / 2;
            const centerY = this.gameHeight / 2;

            // Adjust font size based on screen width
            const baseFontSize = Math.min(48, Math.max(24, this.gameWidth * 0.08));
            
            // Create outer glow
            const outerGlow = this.add.text(centerX, centerY, message, {
                fontSize: `${baseFontSize}px`,
                fontFamily: 'Arial',
                fill: '#fff',
                stroke: color,
                strokeThickness: 16,
                shadow: { blur: 8, color: color, fill: true },
                align: 'center'
            }).setOrigin(0.5).setAlpha(0.4).setScale(scale).setDepth(1000);

            // Create inner glow
            const glow = this.add.text(centerX, centerY, message, {
                fontSize: `${baseFontSize}px`,
                fontFamily: 'Arial',
                fill: '#fff',
                stroke: color,
                strokeThickness: 12,
                shadow: { blur: 6, color: color, fill: true },
                align: 'center'
            }).setOrigin(0.5).setAlpha(0.6).setScale(scale);

            // Create main text
            const text = this.add.text(centerX, centerY, message, {
                fontSize: `${baseFontSize}px`,
                fontFamily: 'Arial',
                fill: color,
                stroke: '#000',
                strokeThickness: 3,
                align: 'center'
            }).setOrigin(0.5).setScale(scale);

            // Add blend effects
            text.setBlendMode(Phaser.BlendModes.ADD);
            glow.setBlendMode(Phaser.BlendModes.SCREEN);
            outerGlow.setBlendMode(Phaser.BlendModes.SCREEN);
            
            // Create a smoother animation
            this.tweens.add({
                targets: [text, glow, outerGlow],
                scaleX: scale * 1.2,
                scaleY: scale * 1.2,
                y: centerY - 20,  // Slight upward movement
                alpha: { from: 1, to: 0 },
                duration: 1500,  // Slower fade
                ease: 'Cubic.easeOut',
                onComplete: () => {
                    text.destroy();
                    glow.destroy();
                    outerGlow.destroy();
                }
            });
        }
    }

    moveHoleRandomly() {
        const margin = Math.min(this.gameWidth, this.gameHeight) * 0.05;
        const minX = margin;
        const maxX = this.gameWidth - margin;
        const minY = margin;
        const maxY = this.gameHeight * 0.7;
        
        let validPosition = false;
        let newX, newY;
        let attempts = 0;

        // If we have a sling direction, use it to determine hole velocity
        if (this.lastSlingAngle !== undefined && this.lastSlingPower !== undefined) {
            // Calculate velocity based on power and screen size
            const baseSpeed = Math.min(this.gameWidth, this.gameHeight) * 0.015 * this.lastSlingPower;
            const velocityX = Math.cos(this.lastSlingAngle) * baseSpeed;
            const velocityY = Math.sin(this.lastSlingAngle) * baseSpeed;
            
            // Apply velocity to hole
            this.matter.body.setVelocity(this.holeBody, { x: velocityX, y: velocityY });
            
            // Clear the sling direction after use
            this.lastSlingAngle = undefined;
            this.lastSlingPower = undefined;
            
            // Add flash effect
            const flash = this.add.circle(this.hole.x, this.hole.y, 40, 0xC0C0C0, 0.5);
            this.tweens.add({
                targets: flash,
                alpha: 0,
                scale: 2,
                duration: 500,
                onComplete: () => flash.destroy()
            });
        }
    }

    showGameOver(isWin = false) {
        this.gameEnded = true;
        
        if (isWin) {
            this.playSound('multiSlay');
        } else {
            // Play a sad trombone sound for losing
            this.playSound('gameOver');
        }

        // Stop any ongoing physics and animations
        this.matter.world.enabled = false;
        this.tweens.killAll();

        // Clean up any existing input handlers
        this.input.removeAllListeners();

        // Clean up all balls
        this.balls.forEach(ball => {
            if (ball.graphics) ball.graphics.destroy();
            if (ball.glow) ball.graphics.destroy();
            if (ball.highlight) ball.highlight.destroy();
            if (ball.body) this.matter.world.remove(ball.body);
        });
        this.balls = [];

        // Clean up all queens
        this.queens.forEach(queen => {
            if (queen.container) queen.container.destroy();
            if (queen.body) this.matter.world.remove(queen.body);
        });
        this.queens = [];

        // Create backdrop with gradient
        const backdrop = this.add.graphics();
        backdrop.fillGradientStyle(0x000000, 0x000000, 0x162447, 0x162447, 0.95);
        backdrop.fillRect(0, 0, this.gameWidth, this.gameHeight);

        let message, color;
        if (isWin) {
            message = 'YASSS QUEEN! 👑\nAll Queens Collected!';
            color = '#ff1493';
            
            // Create particle effect for win using new Phaser 3.60+ API
            const particles = this.add.particles(0, 0, 'particle', {
                x: this.gameWidth / 2,
                y: this.gameHeight / 2,
                speed: { min: 100, max: 200 },
                angle: { min: 0, max: 360 },
                scale: { start: 0.5, end: 0 },
                blendMode: 'ADD',
                lifespan: 1000,
                gravityY: 50,
                frequency: 50,
                quantity: 2,
                emitting: true
            });
            
            // Stop emitting after 3 seconds
            this.time.delayedCall(3000, () => {
                particles.stop();
            });
        } else {
            message = 'GAME OVER\nYou dropped the ball(s)! 🤡';
            color = '#ff0000';

            // Create funny "womp womp" particle effects for losing
            const failParticles = this.add.particles(0, 0, 'particle', {
                x: { min: 0, max: this.gameWidth },
                y: -50,
                speedY: { min: 200, max: 300 },
                speedX: { min: -50, max: 50 },
                angle: 90,
                scale: { start: 0.7, end: 0 },
                tint: [0xff0000, 0xff6666, 0xff9999],
                blendMode: 'ADD',
                lifespan: 2000,
                gravityY: 300,
                frequency: 100,
                quantity: 1,
                emitting: true
            });

            // Add some bouncing sad emojis
            const emojis = ['😭', '😢', '😪', '🤦‍♂️', '🤦‍♀️'];
            emojis.forEach((emoji, i) => {
                const x = this.gameWidth * (0.3 + (i * 0.1));
                const y = this.gameHeight * 0.3;
                const emojiText = this.add.text(x, y, emoji, {
                    fontSize: '40px'
                }).setOrigin(0.5);

                // Make emojis bounce sadly
                this.tweens.add({
                    targets: emojiText,
                    y: y + 30,
                    duration: 1000 + (i * 200),
                    yoyo: true,
                    repeat: -1,
                    ease: 'Bounce.easeOut'
                });
            });

            // Stop particles after 3 seconds
            this.time.delayedCall(3000, () => {
                failParticles.stop();
            });
        }

        // Create text with glow effect
        const textGlow = this.add.text(this.gameWidth / 2, this.gameHeight / 2 - 50, message, {
            fontSize: '48px',
            fontFamily: 'Arial',
            align: 'center',
            fill: '#fff',
            stroke: color,
            strokeThickness: 16,
            shadow: { blur: 8, color: color, fill: true }
        }).setOrigin(0.5).setAlpha(0.4);

        const text = this.add.text(this.gameWidth / 2, this.gameHeight / 2 - 50, message, {
            fontSize: '48px',
            fontFamily: 'Arial',
            align: 'center',
            fill: color,
            stroke: '#000',
            strokeThickness: 3
        }).setOrigin(0.5);

        // Create restart button with neon effect
        const buttonWidth = 200;
        const buttonHeight = 60;
        const buttonX = this.gameWidth / 2;
        const buttonY = this.gameHeight / 2 + 50;

        const buttonContainer = this.add.container(buttonX, buttonY);

        const buttonBg = this.add.rectangle(0, 0, buttonWidth, buttonHeight, 0x2ecc71, 0.2);
        const buttonGlow = this.add.rectangle(0, 0, buttonWidth + 10, buttonHeight + 10, 0x2ecc71, 0.1);
        
        const buttonTextGlow = this.add.text(0, 0, 'Try Again', {
            fontSize: '32px',
            fontFamily: 'Arial',
            fill: '#fff',
            stroke: '#2ecc71',
            strokeThickness: 8
        }).setOrigin(0.5).setAlpha(0.4);

        const buttonText = this.add.text(0, 0, 'Try Again', {
            fontSize: '32px',
            fontFamily: 'Arial',
            fill: '#fff'
        }).setOrigin(0.5);

        buttonContainer.add([buttonGlow, buttonBg, buttonTextGlow, buttonText]);
        buttonContainer.setSize(buttonWidth, buttonHeight);
        buttonContainer.setInteractive();

        // Add hover effects
        buttonContainer.on('pointerover', () => {
            buttonBg.setFillStyle(0x2ecc71, 0.3);
            buttonContainer.setScale(1.05);
        });

        buttonContainer.on('pointerout', () => {
            buttonBg.setFillStyle(0x2ecc71, 0.2);
            buttonContainer.setScale(1);
        });

        // Add click handler
        buttonContainer.on('pointerdown', () => {
            // Clean up game over elements
            backdrop.destroy();
            text.destroy();
            textGlow.destroy();
            buttonContainer.destroy();

            // Re-enable physics for the new game
            this.matter.world.enabled = true;

            // Reset all game state variables
            this.gameEnded = false;
            this.waitingForBallsToSettle = false;
            if (this.ballSettleTimeout) {
                clearTimeout(this.ballSettleTimeout);
                this.ballSettleTimeout = null;
            }

            // Reset game state and restart scene
            this.scene.restart();
        });

        // Add animation
        this.tweens.add({
            targets: [text, textGlow],
            y: text.y - 10,
            duration: 1500,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.inOut'
        });
    }

    checkBallsSettled() {
        let allSettled = true;
        let anyMoving = false;

        this.balls.forEach(ball => {
            if (!ball.body) return;
            
            const velocity = ball.body.velocity;
            const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);
            
            if (speed > 0.1) {
                allSettled = false;
                anyMoving = true;
                ball.moving = true;
            } else {
                ball.moving = false;
            }
        });

        if (allSettled && this.waitingForBallsToSettle) {
            this.waitingForBallsToSettle = false;
            // Clear any existing timeout
            if (this.ballSettleTimeout) {
                clearTimeout(this.ballSettleTimeout);
                this.ballSettleTimeout = null;
            }

            // Only switch to next ball if current ball was used
            if (this.currentBallIndex !== -1 && this.balls[this.currentBallIndex] && this.balls[this.currentBallIndex].used) {
                let nextIndex = this.currentBallIndex + 1;
                this.setActiveBall(nextIndex);
            }
        } else if (anyMoving && this.waitingForBallsToSettle) {
            // Reset the timeout if any ball is still moving
            if (this.ballSettleTimeout) {
                clearTimeout(this.ballSettleTimeout);
            }
            this.ballSettleTimeout = setTimeout(() => {
                this.checkBallsSettled();
            }, 500);
        }
    }

    validateAndFixGameState() {
        // Check if we have any balls left but no active ball
        const remainingBalls = this.balls.filter(ball => ball.body !== null);
        const validBalls = this.balls.filter(ball => !ball.used && !ball.inHole && ball.body);
        const hasActiveBall = this.currentBallIndex >= 0 && 
                            this.currentBallIndex < this.balls.length && 
                            this.balls[this.currentBallIndex] && 
                            !this.balls[this.currentBallIndex].inHole && 
                            !this.balls[this.currentBallIndex].used;

        console.log('Game State Validation:', {
            remainingBalls: remainingBalls.length,
            validBalls: validBalls.length,
            currentIndex: this.currentBallIndex,
            waitingForSettle: this.waitingForBallsToSettle,
            hasActiveBall: hasActiveBall,
            queensHit: this.queensHit,
            totalQueens: this.totalQueens
        });

        // Check for win condition first
        if (!this.gameEnded && this.queensHit === this.totalQueens) {
            this.showGameOver(true);
            return;
        }

        // Only check for lose condition if no balls remain at all
        if (!this.gameEnded && remainingBalls.length === 0 && validBalls.length === 0) {
            const remainingQueens = this.queens.filter(queen => !queen.hit).length;
            if (remainingQueens > 0) {
                this.showGameOver(false);
                return;
            }
        }

        if (remainingBalls.length > 0) {
            // If we have balls but none are valid, reset their states
            if (validBalls.length === 0) {
                console.log('Rescuing game state - resetting ball states');
                remainingBalls.forEach(ball => {
                    if (!ball.inHole) {  // Don't reset balls that are in the hole
                        ball.used = false;
                        ball.moving = false;
                    }
                });
                
                // Force settle state to false
                this.waitingForBallsToSettle = false;
                if (this.ballSettleTimeout) {
                    clearTimeout(this.ballSettleTimeout);
                    this.ballSettleTimeout = null;
                }
                
                // Try to set active ball again
                this.setActiveBall(0);
            }
            // If we have valid balls but no active ball, set one
            else if (!hasActiveBall) {
                console.log('Rescuing game state - setting active ball');
                this.setActiveBall(0);
            }
        }
    }

    update() {
        // Periodically validate game state (every 2 seconds)
        const currentTime = Date.now();
        if (currentTime - this.lastStateCheck > 2000) {
            this.validateAndFixGameState();
            this.lastStateCheck = currentTime;
        }

        // Check for game over condition
        if (!this.gameEnded) {
            const unusedBalls = this.balls.filter(ball => !ball.used && !ball.inHole && ball.body);
            const movingBalls = this.balls.filter(ball => ball.body && ball.moving);
            const remainingQueens = this.queens.filter(queen => !queen.hit).length;

            // Win condition: All queens are slayed
            if (this.queensHit === this.totalQueens && !this.gameEnded) {
                this.showGameOver(true);  // Won - all queens hit
                return;
            }

            // Lose condition: No balls left to use AND queens still remain
            if (unusedBalls.length === 0 && movingBalls.length === 0 && remainingQueens > 0) {
                const remainingBalls = this.balls.filter(ball => ball.body !== null);
                if (remainingBalls.length === 0) {
                    this.showGameOver(false);  // Lost - queens remain and no balls left
                }
            }
        }

        // Update hole position based on physics body
        if (this.holeBody && this.hole) {
            this.hole.setPosition(this.holeBody.position.x, this.holeBody.position.y);
            this.holeGlow.setPosition(this.holeBody.position.x, this.holeBody.position.y);
            this.holeGlow2.setPosition(this.holeBody.position.x, this.holeBody.position.y);
            this.holeRim.setPosition(this.holeBody.position.x, this.holeBody.position.y);

            // Check if active ball is eaten by hole
            if (this.currentBallIndex !== -1 && this.balls[this.currentBallIndex]) {
                const ball = this.balls[this.currentBallIndex];
                if (ball.body && !ball.inHole) {
                    const distanceToHole = Phaser.Math.Distance.Between(
                        ball.graphics.x, ball.graphics.y,
                        this.hole.x, this.hole.y
                    );

                    if (distanceToHole < 25) {
                        // Play nom nom sound
                        this.playSound('nomNom');

                        // Mark ball as in hole
                        ball.inHole = true;
                        this.ballsInHole++;
                        this.scoreText.setText(`Balls: ${this.ballsInHole}/5`);

                        // Add hole consumption effect
                        const consumeEffect = this.add.circle(ball.graphics.x, ball.graphics.y, 15, ball.color);
                        this.tweens.add({
                            targets: consumeEffect,
                            x: this.hole.x,
                            y: this.hole.y,
                            scale: 0.1,
                            alpha: 0,
                            duration: 300,
                            ease: 'Power2',
                            onComplete: () => {
                                consumeEffect.destroy();
                            }
                        });

                        // Remove the ball
                        this.tweens.add({
                            targets: [ball.graphics, ball.glow],
                            alpha: 0,
                            scale: 0.1,
                            duration: 300,
                            onComplete: () => {
                                ball.graphics.destroy();
                                ball.glow.destroy();
                                if (ball.highlight) ball.highlight.destroy();
                                this.matter.world.remove(ball.body);
                                ball.body = null;

                                // Find next available ball
                                let nextBallFound = false;
                                for (let i = 0; i < this.balls.length; i++) {
                                    if (!this.balls[i].inHole && !this.balls[i].used && this.balls[i].body) {
                                        this.setActiveBall(i);
                                        nextBallFound = true;
                                        break;
                                    }
                                }

                                // If no balls available and queens still on field, game over
                                if (!nextBallFound) {
                                    const remainingQueens = this.queens.filter(queen => !queen.hit).length;
                                    if (remainingQueens > 0) {
                                        this.showGameOver(false);
                                    }
                                }
                            }
                        });

                        // Add silver flash effect to the hole
                        const flash = this.add.circle(this.hole.x, this.hole.y, 30, 0xC0C0C0, 0.6);
                        this.tweens.add({
                            targets: flash,
                            alpha: 0,
                            scale: 1.5,
                            duration: 400,
                            onComplete: () => flash.destroy()
                        });
                    }
                }
            }
        }

        // Update ball positions and check for stopping
        this.balls.forEach(ball => {
            if (!ball.body) return;
            
            ball.graphics.x = ball.body.position.x;
            ball.graphics.y = ball.body.position.y;
            ball.glow.x = ball.body.position.x;
            ball.glow.y = ball.body.position.y;
            if (ball.highlight) {
                ball.highlight.x = ball.body.position.x;
                ball.highlight.y = ball.body.position.y;
            }

            // Add velocity damping
            const velocity = ball.body.velocity;
            const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);
            
            if (speed < 0.5) {
                // Stop the ball completely if it's moving very slowly
                this.matter.body.setVelocity(ball.body, { x: 0, y: 0 });
                this.matter.body.setAngularVelocity(ball.body, 0);
                ball.moving = false;
            } else {
                // Ball is moving if it has any significant speed
                ball.moving = true;
                if (speed < 5) {
                    // Apply extra damping when ball is moving slowly
                    const damping = 0.95;
                    this.matter.body.setVelocity(ball.body, {
                        x: velocity.x * damping,
                        y: velocity.y * damping
                    });
                }
            }

            // Check if ball is in hole
            const distanceToHole = Phaser.Math.Distance.Between(
                ball.graphics.x, ball.graphics.y,
                this.hole.x, this.hole.y
            );

            if (distanceToHole < 25 && !ball.inHole) {
                ball.inHole = true;
                this.ballsInHole++;
                this.scoreText.setText(`Balls: ${this.ballsInHole}/5`);
                
                // Add hole consumption effect with silver color
                const consumeEffect = this.add.circle(ball.graphics.x, ball.graphics.y, 15, ball.color);
                this.tweens.add({
                    targets: consumeEffect,
                    x: this.hole.x,
                    y: this.hole.y,
                    scale: 0.1,
                    alpha: 0,
                    duration: 300,
                    ease: 'Power2',
                    onComplete: () => {
                        consumeEffect.destroy();
                    }
                });
                
                // Remove the ball with fade out effect
                this.tweens.add({
                    targets: [ball.graphics, ball.glow],
                    alpha: 0,
                    scale: 0.1,
                    duration: 300,
                    onComplete: () => {
                        ball.graphics.destroy();
                        ball.glow.destroy();
                        this.matter.world.remove(ball.body);
                        ball.body = null;
                    }
                });

                // Add a silver flash effect to the hole
                const flash = this.add.circle(this.hole.x, this.hole.y, 30, 0xC0C0C0, 0.6);
                this.tweens.add({
                    targets: flash,
                    alpha: 0,
                    scale: 1.5,
                    duration: 400,
                    onComplete: () => flash.destroy()
                });
            }
        });

        // Update hole position based on physics body
        if (this.holeBody && this.hole) {
            this.hole.setPosition(this.holeBody.position.x, this.holeBody.position.y);
            this.holeGlow.setPosition(this.holeBody.position.x, this.holeBody.position.y);
            this.holeGlow2.setPosition(this.holeBody.position.x, this.holeBody.position.y);
            this.holeRim.setPosition(this.holeBody.position.x, this.holeBody.position.y);
        }

        // Check for queen hits from balls
        let queensHitThisFrame = [];
        this.queens.forEach(queen => {
            if (!queen.body) return;  // Only skip if physics body is gone

            this.balls.forEach(ball => {
                if (!ball.body) return;

                // Get ball speed
                const velocity = ball.body.velocity;
                const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);

                // Only check for hits if the ball is moving with enough speed
                if (speed > 0.5) {
                    const distance = Phaser.Math.Distance.Between(
                        ball.graphics.x, ball.graphics.y,
                        queen.body.position.x, queen.body.position.y
                    );
                    
                    if (distance < 35 && !queen.hit) {  // Check hit status here
                        queen.hit = true;
                        queensHitThisFrame.push(queen);
                        this.playSound('queenHit');
                    }
                }
            });
        });

        // Process queen hits if any occurred
        if (queensHitThisFrame.length > 0) {
            // Update counters
            this.queensHit += queensHitThisFrame.length;
            this.queensHitThisFling += queensHitThisFrame.length;

            // Remove queens and check for color matches
            let shouldMoveHole = true;
            queensHitThisFrame.forEach(queen => {
                // Find the ball that hit this queen
                const hittingBall = this.balls.find(ball => {
                    if (!ball.body) return false;
                    const distance = Phaser.Math.Distance.Between(
                        ball.graphics.x, ball.graphics.y,
                        queen.body.position.x, queen.body.position.y
                    );
                    return distance < 35;
                });

                // Check if colors match
                if (hittingBall && hittingBall.color === queen.color) {
                    shouldMoveHole = false;
                }

                // Keep physics body during animation but mark as hit
                queen.hit = true;
                
                // Animate the visuals
                this.tweens.add({
                    targets: [queen.graphics, queen.glow],
                    alpha: 0,
                    scale: 1.5,
                    duration: 500,
                    onComplete: () => {
                        // Remove physics body after animation completes
                        if (queen.body) {
                            this.matter.world.remove(queen.body);
                            queen.body = null;
                        }
                        queen.graphics.destroy();
                        queen.glow.destroy();
                    }
                });
            });

            // Move hole only if no color matches occurred
            if (shouldMoveHole) {
                this.moveHoleRandomly();
            }

            // Update the counter
            this.queensText.setText(`Queens hit: ${this.queensHit}/${this.totalQueens}`);

            // Show multi-slay message if we hit multiple queens in this fling
            if (this.queensHitThisFling >= 2) {
                this.showSlayMessage(this.queensHitThisFling);
            }
        }

        // Update queen positions and check for hole collisions
        this.queens.forEach(queen => {
            if (queen.body) {
                queen.container.setPosition(queen.body.position.x, queen.body.position.y);
                
                // Check if queen is touching the hole
                const distanceToHole = Phaser.Math.Distance.Between(
                    queen.body.position.x, queen.body.position.y,
                    this.hole.x, this.hole.y
                );

                if (distanceToHole < 35 && !queen.hit) {  // Only check non-hit queens for hole collision
                    // Mark queen as hit
                    queen.hit = true;
                    this.queensHit++;
                    this.queensHitThisFling++;
                    this.queensText.setText(`Queens hit: ${this.queensHit}/${this.totalQueens}`);

                    // Add hole consumption effect
                    const consumeEffect = this.add.circle(queen.container.x, queen.container.y, 20, queen.color);
                    this.tweens.add({
                        targets: consumeEffect,
                        x: this.hole.x,
                        y: this.hole.y,
                        scale: 0.1,
                        alpha: 0,
                        duration: 300,
                        ease: 'Power2',
                        onComplete: () => {
                            consumeEffect.destroy();
                        }
                    });

                    // Add silver flash effect to the hole
                    const flash = this.add.circle(this.hole.x, this.hole.y, 35, 0xC0C0C0, 0.6);
                    this.tweens.add({
                        targets: flash,
                        alpha: 0,
                        scale: 1.5,
                        duration: 400,
                        onComplete: () => flash.destroy()
                    });

                    // Animate queen disappearing
                    this.tweens.add({
                        targets: queen.container,
                        alpha: 0,
                        scale: 0.1,
                        duration: 300,
                        onComplete: () => {
                            // Remove physics body after animation completes
                            if (queen.body) {
                                this.matter.world.remove(queen.body);
                                queen.body = null;
                            }
                            queen.container.destroy();
                        }
                    });

                    // Play sound effect
                    this.playSound('queenHit');

                    // Check if any active ball matches the queen's color
                    const matchingBall = this.balls.find(ball => 
                        ball.body && !ball.inHole && ball.color === queen.color
                    );

                    // Only move hole if there's no color match
                    if (!matchingBall) {
                        // Apply a small impulse to the hole after consuming queen
                        const randomAngle = Math.random() * Math.PI * 2;
                        const impulseStrength = 5;
                        this.matter.body.setVelocity(this.holeBody, {
                            x: Math.cos(randomAngle) * impulseStrength,
                            y: Math.sin(randomAngle) * impulseStrength
                        });
                    }

                    // Show multi-slay message if applicable
                    if (this.queensHitThisFling >= 2) {
                        this.showSlayMessage(this.queensHitThisFling);
                    }

                    // Check for game win condition
                    if (this.queensHit === this.totalQueens) {
                        this.showGameOver(true);
                    }
                }
            }
        });

        // Check for game over condition - all balls gone and no queens hit
        const activeBallsCount = this.balls.filter(ball => ball.body !== null).length;
        if (!this.gameEnded && activeBallsCount === 0) {
            if (this.queensHit === 0) {
                this.showGameOver(false);  // Game Over - Lost
            } else if (this.queensHit === this.totalQueens) {
                this.showGameOver(true);   // Game Over - Won
            }
        }

        // Update rubber band if dragging
        if (this.isDragging && this.activeBall) {
            this.rubberBand.clear();
            this.rubberBand.lineStyle(2, 0xffffff);
            this.rubberBand.beginPath();
            this.rubberBand.moveTo(this.activeBall.graphics.x, this.activeBall.graphics.y);
            
            const dragPoint = this.constrainDragPoint(
                this.input.activePointer,
                { x: this.activeBall.graphics.x, y: this.activeBall.graphics.y }
            );
            
            this.rubberBand.lineTo(dragPoint.x, dragPoint.y);
            this.rubberBand.strokePath();
        }

        // Check if balls have settled
        if (this.waitingForBallsToSettle) {
            this.checkBallsSettled();
        }

        // Check for ball collisions
        if (!this.lastBallHitTime) {
            this.lastBallHitTime = 0;
        }

        this.matter.world.on('collisionstart', (event) => {
            event.pairs.forEach((pair) => {
                const bodyA = pair.bodyA;
                const bodyB = pair.bodyB;

                // Ball-to-ball collision
                if (bodyA.label === 'Circle Body' && bodyB.label === 'Circle Body') {
                    const velocity = Math.sqrt(
                        Math.pow(bodyA.velocity.x - bodyB.velocity.x, 2) +
                        Math.pow(bodyA.velocity.y - bodyB.velocity.y, 2)
                    );
                    
                    // Only play sound if enough time has passed since last hit (100ms minimum)
                    const currentTime = Date.now();
                    if (velocity > 1 && currentTime - this.lastBallHitTime > 100) {
                        this.playSound('ballHit');
                        this.lastBallHitTime = currentTime;
                    }
                }
            });
        });
    }

    resize(gameSize) {
        const oldWidth = this.gameWidth;
        const oldHeight = this.gameHeight;
        
        // Update game dimensions
        this.gameWidth = gameSize.width;
        this.gameHeight = gameSize.height;
        
        // Recalculate margins and play area
        const margin = Math.min(this.gameWidth, this.gameHeight) * 0.05;
        
        // Store existing game objects' positions relative to play area
        const gameObjects = [];
        if (this.matter && this.matter.world) {
            this.matter.world.localWorld.bodies.forEach(body => {
                if (body.label !== 'Rectangle Body') { // Skip boundary bodies
                    const relativeX = (body.position.x - margin) / (oldWidth - margin * 2);
                    const relativeY = (body.position.y - margin) / (oldHeight - margin * 2);
                    gameObjects.push({
                        body,
                        relativeX,
                        relativeY,
                        velocity: { x: body.velocity.x, y: body.velocity.y },
                        isStatic: body.isStatic
                    });
                }
            });
            
            // Clear only the boundary bodies
            this.matter.world.localWorld.bodies = this.matter.world.localWorld.bodies.filter(
                body => body.label !== 'Rectangle Body'
            );
            
            // Add new bounds with proper dimensions
            this.matter.world.setBounds(
                margin, 
                margin, 
                this.gameWidth - margin * 2, 
                this.gameHeight - margin * 2, 
                32,
                {
                    restitution: 0.8,
                    friction: 0.05
                }
            );
            
            // Reposition game objects in new play area
            gameObjects.forEach(obj => {
                const newX = margin + (this.gameWidth - margin * 2) * obj.relativeX;
                const newY = margin + (this.gameHeight - margin * 2) * obj.relativeY;
                
                this.matter.body.setPosition(obj.body, {
                    x: newX,
                    y: newY
                });
                
                // Restore velocity and static state
                if (!obj.isStatic) {
                    this.matter.body.setVelocity(obj.body, obj.velocity);
                }
            });
        }

        // Update playfield graphics if they exist
        if (this.playfield) {
            this.playfield.clear();
            this.playfield.lineStyle(4, 0x2ecc71);
            this.playfield.strokeRect(
                margin,
                margin,
                this.gameWidth - margin * 2,
                this.gameHeight - margin * 2
            );
        }

        // Update hole position
        if (this.hole) {
            const holeY = margin + (this.gameHeight - margin * 2) * 0.2; // 20% from top
            this.hole.setPosition(this.gameWidth/2, holeY);
            this.holeGlow.setPosition(this.gameWidth/2, holeY);
            this.holeGlow2.setPosition(this.gameWidth/2, holeY);
            this.holeRim.setPosition(this.gameWidth/2, holeY);
        }

        // Update visual elements for balls
        this.balls.forEach(ball => {
            if (ball.body) {
                ball.graphics.setPosition(ball.body.position.x, ball.body.position.y);
                ball.glow.setPosition(ball.body.position.x, ball.body.position.y);
                if (ball.highlight) {
                    ball.highlight.setPosition(ball.body.position.x, ball.body.position.y);
                }
            }
        });

        // Update visual elements for queens
        this.queens.forEach(queen => {
            if (queen.body && !queen.hit) {
                queen.container.setPosition(queen.body.position.x, queen.body.position.y);
            }
        });

        // Recalculate number of queens and play area
        this.calculatePlayAreaAndQueens();

        // Update UI positions
        if (this.scoreText) {
            this.scoreText.setPosition(16, 16);
            this.queensText.setPosition(16, 56);
            this.flingText.setPosition(16, 96);
        }
    }

    debugBallStates() {
        console.log('=== Ball States ===');
        this.balls.forEach((ball, index) => {
            console.log(`Ball ${index}:`, {
                exists: !!ball.body,
                used: ball.used,
                inHole: ball.inHole,
                moving: ball.moving
            });
        });
        console.log('Current ball index:', this.currentBallIndex);
        console.log('Waiting for settle:', this.waitingForBallsToSettle);
    }

    playSound(type) {
        if (!this.soundEnabled || !this.audioContext) return;

        // Ensure audio context is running (needed for Chrome)
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }

        // Clean up any existing nodes
        if (this.currentGain) {
            this.currentGain.disconnect();
        }

        const now = this.audioContext.currentTime;
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        
        // Store current nodes for cleanup
        this.currentOscillator = oscillator;
        this.currentGain = gainNode;
        
        gainNode.connect(this.masterGain);
        oscillator.connect(gainNode);

        switch(type) {
            case 'nomNom':
                // Create a fun "nom nom" sound
                oscillator.type = 'sine';
                const nomDuration = 0.15;
                
                // First "nom"
                oscillator.frequency.setValueAtTime(300, now);
                oscillator.frequency.linearRampToValueAtTime(200, now + nomDuration * 0.5);
                gainNode.gain.setValueAtTime(0.3, now);
                gainNode.gain.linearRampToValueAtTime(0.01, now + nomDuration);
                
                // Second "nom"
                oscillator.frequency.setValueAtTime(250, now + nomDuration);
                oscillator.frequency.linearRampToValueAtTime(150, now + nomDuration * 1.5);
                gainNode.gain.setValueAtTime(0.3, now + nomDuration);
                gainNode.gain.linearRampToValueAtTime(0.01, now + nomDuration * 2);
                
                oscillator.start(now);
                oscillator.stop(now + nomDuration * 2);
                break;

            case 'gameOver':
                // Sad trombone sound
                oscillator.type = 'sawtooth';
                oscillator.frequency.setValueAtTime(440, now);
                oscillator.frequency.linearRampToValueAtTime(220, now + 0.3);
                oscillator.frequency.setValueAtTime(196, now + 0.4);
                oscillator.frequency.setValueAtTime(165, now + 0.6);
                gainNode.gain.setValueAtTime(0.3, now);
                gainNode.gain.linearRampToValueAtTime(0.01, now + 0.8);
                oscillator.start(now);
                oscillator.stop(now + 0.8);
                break;

            case 'multiSlay':
                // Achievement sound with ascending notes
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(440, now);
                oscillator.frequency.setValueAtTime(880, now + 0.1);
                oscillator.frequency.setValueAtTime(1320, now + 0.2);
                gainNode.gain.setValueAtTime(0.3, now);
                gainNode.gain.linearRampToValueAtTime(0.01, now + 0.3);
                oscillator.start(now);
                oscillator.stop(now + 0.3);
                break;

            case 'queenHit':
                // Explosion-like sound
                oscillator.type = 'sawtooth';
                oscillator.frequency.setValueAtTime(440, now);
                oscillator.frequency.exponentialRampToValueAtTime(44, now + 0.3);
                gainNode.gain.setValueAtTime(0.3, now);
                gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
                oscillator.start(now);
                oscillator.stop(now + 0.3);
                break;
            case 'ballHit':
                // Short click sound (previously ballHit sound)
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(220, now);
                oscillator.frequency.exponentialRampToValueAtTime(880, now + 0.1);
                gainNode.gain.setValueAtTime(0.3, now);
                gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
                oscillator.start(now);
                oscillator.stop(now + 0.1);
                break;

            case 'holeScore':
                // Score sound
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(880, now);
                oscillator.frequency.exponentialRampToValueAtTime(1760, now + 0.1);
                gainNode.gain.setValueAtTime(0.2, now);
                gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
                oscillator.start(now);
                oscillator.stop(now + 0.1);
                break;

            case 'bgMusic':
                // Simple background pattern
                const pattern = [440, 554, 659, 554];
                const duration = 0.2;
                pattern.forEach((freq, i) => {
                    const noteOsc = this.audioContext.createOscillator();
                    const noteGain = this.audioContext.createGain();
                    noteOsc.connect(noteGain);
                    noteGain.connect(this.masterGain);
                    
                    noteOsc.type = 'sine';
                    noteOsc.frequency.setValueAtTime(freq, now + i * duration);
                    
                    noteGain.gain.setValueAtTime(0.1, now + i * duration);
                    noteGain.gain.exponentialRampToValueAtTime(0.01, now + (i + 1) * duration);
                    
                    noteOsc.start(now + i * duration);
                    noteOsc.stop(now + (i + 1) * duration);
                });
                
                // Loop the pattern
                setTimeout(() => {
                    if (!this.gameEnded) this.playSound('bgMusic');
                }, pattern.length * duration * 1000);
                break;
        }

        // Add cleanup handler for all non-bgMusic sounds
        if (type !== 'bgMusic') {
            oscillator.onended = () => {
                oscillator.disconnect();
                gainNode.disconnect();
                this.currentOscillator = null;
                this.currentGain = null;
            };
        }
    }
}

const config = {
    type: Phaser.AUTO,
    scale: {
        mode: Phaser.Scale.RESIZE,
        parent: 'game',
        width: '100%',
        height: '100%',
        autoCenter: Phaser.Scale.CENTER_BOTH,
        fullscreenTarget: 'game'
    },
    backgroundColor: '#000000',
    physics: {
        default: 'matter',
        matter: {
            gravity: { y: 0 },
            debug: false,
            enableSleeping: false,
            positionIterations: 10,
            velocityIterations: 8,
            constraintIterations: 4
        }
    },
    input: {
        activePointers: 2,
        touch: {
            capture: true,
            dragDistanceThreshold: 2
        }
    },
    scene: MainScene
};

const game = new Phaser.Game(config);

// Handle mobile touch events
if (game.device.os.android || game.device.os.iOS) {
    // Handle orientation changes
    window.addEventListener('orientationchange', () => {
        setTimeout(() => {
            game.scale.refresh();
        }, 100);
    });

    // Prevent default touch behaviors
    document.addEventListener('touchmove', (e) => {
        e.preventDefault();
    }, { passive: false });

    // Prevent double tap zoom
    let lastTap = 0;
    document.addEventListener('touchend', (e) => {
        const currentTime = new Date().getTime();
        const tapLength = currentTime - lastTap;
        if (tapLength < 500 && tapLength > 0) {
            e.preventDefault();
        }
        lastTap = currentTime;
    });
} 